import { Trans } from '@lingui/react/macro';

import Badge from '@/design_system/Badge';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import { Separator } from '@/design_system/Menu/Menu';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconArrow from '@/icons/Arrow.svg';
import IconStoreRepair from '@/icons/StoreRepair.svg';
import IconTask from '@/icons/Task.svg';
import { useUniqueArticleHistory } from '@/models/uniqueArticle';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';
import { formatDate } from '@/utils/date';
import { Currency, formatCurrency } from '@/utils/number';

export const RequestsHistory = () => {
  const { article } = useArticleContext();
  const { data: uniqueArticleHistory } = useUniqueArticleHistory(article.uniqueArticle?.id);

  return (
    <div>
      {!uniqueArticleHistory?.requestsHistory.length ? (
        <InlineText size="200" color="secondary">
          <Trans id="requests.new.articles.drawer.history.requests-history.no-requests">
            No requests to display
          </Trans>
        </InlineText>
      ) : (
        <Stack gap="0.5rem">
          {uniqueArticleHistory.requestsHistory.map((requestHistory, key) => {
            return <RequestHistoryCard key={key} requestHistory={requestHistory} />;
          })}
        </Stack>
      )}
    </div>
  );
};

type RequestHistoryType = 'pre-sales' | 'after-sales';
type RequestHistoryArticle = {
  id: string;
  workshopName?: string;
  price?: {
    amount: number;
    currency: Currency;
  };
  status?: 'refused' | 'archived';
  actions: { name?: I18nWording | null; customDescription?: string | null }[];
};

const RequestHistoryCard = ({
  requestHistory,
}: {
  requestHistory: {
    id: string;
    reference: string | null;
    externalReference: string | null;
    store: { name: string } | null;
    type: RequestHistoryType;
    canAccess: boolean;
    createdAt: string;
    requestArticle: RequestHistoryArticle;
  };
}) => {
  return (
    <Box padding="0.5rem 0.75rem 0.75rem" style={{ gap: '0.5rem' }}>
      <RequestHistoryHeader
        requestReference={requestHistory.reference ?? requestHistory.externalReference}
        storeName={requestHistory.store?.name}
        requestCreatedAt={requestHistory.createdAt}
      />

      <RequestHistoryDetails
        actions={requestHistory.requestArticle.actions}
        workshopName={requestHistory.requestArticle.workshopName}
      />

      <RequestHistoryBadges
        type={requestHistory.type}
        requestArticle={requestHistory.requestArticle}
      />

      {requestHistory.canAccess && !!requestHistory.reference && (
        <>
          <span style={{ padding: '0.25rem  0' }}>
            <Separator />
          </span>

          <GoToRequestButton
            requestId={requestHistory.id}
            requestArticleId={requestHistory.requestArticle.id}
          />
        </>
      )}
    </Box>
  );
};

const RequestHistoryHeader = ({
  requestReference,
  storeName,
  requestCreatedAt,
}: {
  requestReference: string | null;
  storeName?: string;
  requestCreatedAt: string;
}) => (
  <div>
    <Stack row justifyContent="space-between" alignItems="center">
      <InlineText size="200">{requestReference}</InlineText>
      <InlineText size="200" color="disabled">
        {formatDate(new Date(requestCreatedAt), {
          dateStyle: 'medium',
        })}
      </InlineText>
    </Stack>

    <InlineText>{storeName}</InlineText>
  </div>
);

const RequestHistoryDetails = ({
  actions,
  workshopName,
}: {
  actions: { name?: I18nWording | null; customDescription?: string | null }[];
  workshopName?: string;
}) => {
  const { _db } = useDatabaseTranslation();

  return (
    <Stack gap="0.5rem">
      {!!actions.length && (
        <Stack row gap="0.5rem">
          {actions.map((action, key) => (
            <Badge key={key} size="small" borderRadius="soft" hasBorder color="neutral">
              <IconTask />
              {action.name ? _db(action.name) : action.customDescription}
            </Badge>
          ))}
        </Stack>
      )}

      {!!workshopName && (
        <div>
          <Badge size="small" borderRadius="soft" hasBorder color="neutral">
            <IconStoreRepair />
            {workshopName}
          </Badge>
        </div>
      )}
    </Stack>
  );
};

const RequestHistoryBadges = ({
  type,
  requestArticle,
}: {
  type: RequestHistoryType;
  requestArticle: RequestHistoryArticle;
}) => (
  <Stack row gap="0.5rem" alignItems="center">
    <Badge size="small" hasBorder color={type === 'pre-sales' ? 'neutral' : 'secondary'}>
      {type === 'pre-sales' ? (
        <Trans id="requests.new.articles.drawer.history.requests-history.card.pre-sales">
          Pre-Sales
        </Trans>
      ) : (
        <Trans id="requests.new.articles.drawer.history.requests-history.card.after-sales">
          After-Sales
        </Trans>
      )}
    </Badge>

    {!!requestArticle.status && (
      <Badge size="small" hasBorder color="danger">
        {requestArticle.status === 'refused' ? (
          <Trans id="requests.new.articles.drawer.history.requests-history.card.refused">
            Refused
          </Trans>
        ) : (
          <Trans id="requests.new.articles.drawer.history.requests-history.card.archived">
            Archived
          </Trans>
        )}
      </Badge>
    )}

    {requestArticle.status !== 'refused' &&
      !!requestArticle.price &&
      requestArticle.price.amount > 0 && (
        <Badge size="small" hasBorder color="primary">
          <Trans id="requests.new.articles.drawer.history.requests-history.card.paid">Paid</Trans> (
          {formatCurrency(requestArticle.price.amount, requestArticle.price.currency)})
        </Badge>
      )}

    {requestArticle.status !== 'refused' &&
      !!requestArticle.price &&
      requestArticle.price.amount === 0 && (
        <Badge size="small" hasBorder color="tertiary">
          <Trans id="requests.new.articles.drawer.history.requests-history.card.free">Free</Trans>
        </Badge>
      )}
  </Stack>
);

const GoToRequestButton = ({
  requestId,
  requestArticleId,
}: {
  requestId: string;
  requestArticleId: string;
}) => (
  <Button
    variant="style-less"
    style={{ display: 'inline-block' }}
    onPress={() => window.open(`/requests/${requestId}/articles/${requestArticleId}`, '_blank')}
  >
    <Stack row gap="0.125rem" alignItems="center">
      <InlineText size="200" color="secondary">
        <Trans id="requests.new.articles.drawer.history.requests-history.card.go-to-request">
          Go to request
        </Trans>
      </InlineText>

      <IconArrow right />
    </Stack>
  </Button>
);
